export const eventTypes = ['All', 'Webinar', 'Workshop', 'Conference', 'Event'];

export const events = [
  {
    slug: 'private-secrets-management-webinar',
    title: 'Private secrets management webinar',
    type: 'Webinar',
    status: 'upcoming',
    startDate: '2026-07-15',
    endDate: '2026-07-15',
    displayDate: 'Wednesday, 15 July 2026',
    displayDateRange: '15 July 2026',
    time: '11:00 AM PDT / 2:00 PM ET',
    duration: '45 minutes + Q&A',
    location: 'Online',
    excerpt:
      'Why production secrets belong in private environments you govern - reducing regulatory exposure, privacy risk, and strategic dependency as headline incidents expose third-party control planes.',
    seoDescription:
      'Register for the Ametnes webinar on private secrets management: PKI, enterprise secrets governance across applications, databases, and CI/CD, and reducing dependency on third-party control planes.',
    platforms: ['HashiCorp Vault', 'OpenBao', 'Infisical'],
    agenda: [
      'Lessons from recent SaaS breaches - OAuth, env vars, and blast radius',
      'Designing secrets management for private environments - whether driven by data privacy preferences or regulatory requirements',
      'PKI and enterprise secrets governance across applications, databases, and CI/CD pipelines',
      'Practical patterns: encryption, rotation, audit, and least-privilege access',
      'How Ametnes helps teams deploy and operate private secrets infrastructure',
    ],
    description: [
      'Platform-hosted secrets are convenient until they become someone else\'s blast radius. Private secrets management - with HashiCorp Vault, OpenBao, or Infisical running in your cluster, VPC, or on-prem - keeps secrets out of multi-tenant control planes.',
      'This session goes beyond vault deployment to cover PKI, certificate lifecycle, and enterprise secrets governance across applications, databases, and CI/CD pipelines. Recent headlines around SaaS breaches make the case: critical dependencies should live where you set the rules.',
    ],
    audience:
      'CISOs, platform leaders, security architects, and engineering teams re-evaluating how secrets, certificates, and credentials are governed across applications, databases, and delivery pipelines.',
    registerPath: '/events/private-secrets-management-webinar/register',
  },
  {
    slug: 'sovereign-ai-in-private-environments',
    title: 'Sovereign AI in private environments',
    type: 'Webinar',
    status: 'upcoming',
    startDate: '2026-07-29',
    endDate: '2026-07-29',
    displayDate: 'Wednesday, 29 July 2026',
    displayDateRange: '29 July 2026',
    time: '11:00 AM PDT / 2:00 PM ET / AST',
    duration: '45 minutes + Q&A',
    location: 'Online',
    excerpt:
      'Governments and regulated industries need sovereign AI as they embed models into critical workflows - recent events around Anthropic\'s Fable 5 show that hosted providers can revoke access overnight when policy shifts. Learn how to build governed, private AI on open-source tooling you control.',
    seoDescription:
      'Register for the Ametnes sovereign AI webinar: private AI infrastructure for government and regulated industries, guardrails, private inference, and why the Anthropic Fable 5 suspension makes self-hosted AI architecture urgent for public sector, finance, and telecom.',
    agenda: [
      'Why AI sovereignty matters now: lessons from the Anthropic Fable 5 suspension and government access directives',
      'Why sensitive workloads need private AI infrastructure, not scattered public API keys',
      'The six-layer open-source sovereign AI reference architecture',
      'Governance vs guardrails: policy, PII controls, human-in-the-loop, and audit',
      'Protecting data at rest, in transit, and at inference time',
      'Hybrid model strategy: one gateway, public and private routes by classification',
      'How Ametnes helps teams deploy and operate sovereign AI platforms',
    ],
    description: [
      'Governments and regulated enterprises are moving AI from pilots into core workflows - procurement, citizen services, fraud detection, network operations, and policy analysis. That shift makes sovereign AI a strategic requirement, not a nice-to-have. When inference depends on a hosted provider\'s control plane, your access can change without notice.',
      'Recent events around Anthropic\'s Fable 5 and Mythos 5 models underscore the risk: a U.S. government export-control directive required suspending access for foreign nationals, and because nationality could not be verified per request, the models were disabled globally for all customers - including workflows that had just adopted them. For public-sector and sovereign-cloud teams, the lesson is clear: critical processes cannot rest on models you do not host and cannot guarantee will remain available.',
      'This session walks through a practical open-source sovereign AI stack: gateway and routing, private model serving, RAG on approved corpora, guardrails for PII and secrets, and monitoring that ties usage to teams and use cases. You will see how to treat AI as shared infrastructure rather than one-off copilots with shared keys - and how to keep sensitive tiers on private inference inside a boundary you govern.',
      'Whether you are replacing shadow consumer chat tools or designing a sovereign AI platform for government, finance, or telecom, the same layered pattern applies: decouple applications from vendors, enforce policy at the gateway, and retain the option to run models locally when geopolitics or regulation makes hosted access unreliable.',
    ],
    audience:
      'Government digital leaders, CISOs, chief data officers, platform leaders, and architects in regulated or data-sensitive organizations - especially public sector, finance, and telecom - building AI into production workflows and re-evaluating dependency on hosted frontier models after recent provider access restrictions.',
    registerPath: '/events/sovereign-ai-in-private-environments/register',
  },
  {
    slug: 'observability-at-scale',
    title: 'Observability at scale',
    type: 'Webinar',
    status: 'upcoming',
    startDate: '2026-08-12',
    endDate: '2026-08-12',
    displayDate: 'Wednesday, 12 August 2026',
    displayDateRange: '12 August 2026',
    time: '11:00 AM PDT / 2:00 PM ET',
    duration: '45 minutes + Q&A',
    location: 'Online',
    excerpt:
      'When ingest bills and retention tiers make SaaS observability unsustainable - how open-source stacks run privately can cut cost, keep telemetry under your control, and meet regulatory requirements for organizations operating at scale.',
    seoDescription:
      'Register for the Ametnes webinar on observability at scale: open-source Grafana, Prometheus, OpenTelemetry, and GreptimeDB in private environments - reducing SaaS ingest and retention costs while meeting regulatory requirements.',
    platforms: ['Grafana', 'Prometheus', 'OpenTelemetry', 'GreptimeDB'],
    agenda: [
      'The real cost of SaaS observability at scale: ingest, retention, query, and egress charges',
      'Why open-source observability in private environments changes the economics - and the control model',
      'Systems monitoring and management for regulated industries that cannot rely on multi-tenant SaaS',
      'Architecture patterns for cost-controlled ingestion, retention, and query with open-source tooling',
      'How AI can simplify triage and operations without adding another expensive proprietary layer',
      'How Ametnes helps teams deploy and operate private open-source observability platforms',
    ],
    description: [
      'Cloud SaaS observability platforms are convenient until telemetry volume makes the bill the problem: per-gigabyte ingest, tiered retention, query overages, and cross-region egress can turn observability into one of the fastest-growing infrastructure line items.',
      'Open-source observability - Grafana, Prometheus, OpenTelemetry, GreptimeDB, and the surrounding stack - gives organizations a path to run systems monitoring and management in environments they govern, with predictable cost and without vendor lock-in. For highly regulated industries, that same model keeps telemetry inside approved boundaries.',
      'This session covers when SaaS economics break down, how private open-source architectures restore cost control, and how AI can reduce the operational burden of running observability at scale in your own environment.',
    ],
    audience:
      'Platform leaders, SRE and observability teams, and architects in data-intensive or regulated organizations comparing SaaS observability bills against private open-source alternatives.',
    registerPath: '/events/observability-at-scale/register',
  },
  {
    slug: 'agentic-ai-in-private-environments',
    title: 'Agentic AI in private, controlled environments',
    type: 'Webinar',
    status: 'upcoming',
    startDate: '2026-09-16',
    endDate: '2026-09-16',
    displayDate: 'Wednesday, 16 September 2026',
    displayDateRange: '16 September 2026',
    time: '11:00 AM PDT / 2:00 PM ET',
    duration: '45 minutes + Q&A',
    location: 'Online',
    excerpt:
      "Agentic AI applications read your data, call your tools, and take actions on their own - which is why they should run where you control the perimeter. Learn how to provision private AI application infrastructure and connect agents to private data sources for RAG.",
    seoDescription:
      "Register for the Ametnes webinar on building agentic AI applications in private environments: provisioning private AI application infrastructure, RAG on private data sources, and keeping agent workflows inside your perimeter.",
    platforms: ['LiteLLM', 'PostgreSQL', 'pgvector'],
    agenda: [
      'Why agentic AI changes the security model: agents read, call, and act - not just answer',
      'Provisioning private AI application infrastructure: gateway, model serving, and runtime inside your perimeter',
      'Connecting agents to private data sources for RAG - vector stores, embeddings, and retrieval without data egress',
      'Data as the first line of defence: access controls, audit, and governance for agent workflows',
      'Reference pattern: an agent that routes, retrieves, and acts without leaving your environment',
      'How Ametnes helps teams deploy and operate private AI application infrastructure',
    ],
    description: [
      "A chatbot answers questions. An agent reads your data, calls your tools, and takes actions on its own. That autonomy is what makes agentic AI so powerful - and it is exactly why agent workflows should run in an environment where you control the perimeter.",
      "This session focuses on building agentic AI applications in private, controlled environments. We walk through provisioning private AI application infrastructure - the gateway, model serving, and runtime your agents need - and connecting those agents to private data sources for retrieval-augmented generation (RAG), without moving your data into a third-party control plane.",
      "For organisations bound by data sovereignty regulation, or teams that simply prefer to keep their data in their own environment as the first line of defence, the pattern is the same: decouple applications from hosted providers, keep retrieval and inference inside your boundary, and enforce policy where the agent acts.",
    ],
    audience:
      "Platform leaders, AI/ML engineers, and architects building agentic AI applications in regulated or data-sensitive organisations - especially teams bound by data sovereignty rules or that keep sensitive data in private environments as a first line of defence.",
    registerPath: '/events/agentic-ai-in-private-environments/register',
  },
];

export const getEventBySlug = (slug) => events.find((event) => event.slug === slug);

export const getSortedEvents = (filterType = 'All') => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const current = events.filter((event) => new Date(event.endDate) >= today);

  const filtered =
    filterType === 'All' ? current : current.filter((event) => event.type === filterType);

  return [...filtered].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
};
